// src/components/dashboard/employer/needs/NeedDetail.tsx
// Employer job-posting detail — renders through the shared NeedView.
import React, { useState, useEffect } from 'react';
import { useEmployer } from '@/contexts/EmployerContext';
import { useNavigate, useParams } from 'react-router-dom';
import { Need, NeedStatus, getNeedStatusLabel } from '@/lib/models/need';
import NeedFormModal from '@/components/dashboard/employer/needs/NeedFormModal';
import RelistNeedDialog from '@/components/dashboard/employer/needs/RelistNeedDialog';
import NeedView, { needToView } from '@/components/needs/NeedView';
import { needApi } from '@/lib/api/needApi';
import { prerequisiteApi } from '@/lib/api/prerequisiteApi';
import { onboardingApi } from '@/lib/api/onboardingApi';
import { moduleApi } from '@/lib/api/moduleApi';
import { ArrowLeft, BookOpen, GraduationCap } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import NeedApplicationsSection from '@/components/dashboard/employer/applications/NeedApplicationsSection';
import { useLinkLauncher } from '@/contexts/LinkLauncherContext';
import { EntityActionMenu } from '@/components/custom-ui/EntityActionMenu';
import { buildNeedMenuItems, toEntityActions } from './needMenuActions';
import {
    LoadingState, ErrorState, StatusBadge, ConfirmDialog,
} from '@/components/custom-ui';
import { NEXILINK_CARD_WHITE_ELEVATED, NEXILINK_SPACING } from '@/lib/nexilink-card-styles';

const NeedDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { getNeedById, updateNeedStatus, deleteNeed, employerDetails } = useEmployer();
    const navigate = useNavigate();
    const { stageLink } = useLinkLauncher();
    const { toast } = useToast();

    // #265 — copy the PUBLIC shareable link (`/jobb/:id`). Only offered while the
    // posting is active/published, since the public page 404s for any other status.
    const copyPublicLink = () => {
        const url = `${window.location.origin}/jobb/${need!.id}`;
        navigator.clipboard.writeText(url).then(
            () => toast({ title: 'Länk kopierad', description: 'Delningslänken till annonsen finns i urklipp.' }),
            () => toast({ title: 'Kunde inte kopiera länken', description: url, variant: 'destructive' }),
        );
    };

    const [need, setNeed] = useState<Need | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

    // Attachments (read-only here; managed in the form — Phase D)
    const [requiredCourses, setRequiredCourses] = useState<string[]>([]);
    const [programName, setProgramName] = useState<string | null>(null);

    useEffect(() => { if (id) loadNeed(); }, [id]);

    const loadNeed = async () => {
        if (!id) return;
        try {
            setLoading(true);
            setError(null);
            const loadedNeed = await getNeedById(id);
            setNeed(loadedNeed);
            void loadAttachments(loadedNeed);
        } catch (err) {
            console.error('Failed to load need:', err);
            setError('Kunde inte ladda annonsen');
        } finally {
            setLoading(false);
        }
    };

    const loadAttachments = async (n: Need) => {
        // Required courses (course-based prerequisites)
        try {
            const [required, modules] = await Promise.all([
                prerequisiteApi.getRequiredModules(n.id),
                moduleApi.getMyModules(),
            ]);
            const nameById = new Map((modules || []).map((m) => [m.id, m.module_name]));
            setRequiredCourses(required.map((r) => nameById.get(r.module_id) || 'Modul'));
        } catch { /* ignore */ }
        // Onboarding program
        if (n.onboarding_program_id) {
            onboardingApi.getProgramById(n.onboarding_program_id).then((p) => setProgramName(p?.name ?? null)).catch(() => {});
        } else {
            setProgramName(null);
        }
    };

    const handleBack = () => navigate('/dashboard/needs');

    const handleEditSuccess = (updatedNeed?: Need) => {
        if (updatedNeed) { setNeed(updatedNeed); void loadAttachments(updatedNeed); }
        else loadNeed();
    };
    const handleDuplicateSuccess = (newNeed?: Need) => { if (newNeed) navigate(`/dashboard/needs/${newNeed.id}`); };

    // #281/#64 — EVERY status transition goes through an "Är du säker?" confirm.
    // A pending transition holds the target status + the copy; the shared
    // ConfirmDialog renders it. updateNeedStatus (context) returns null on failure
    // instead of throwing, so we check the result and surface a toast — otherwise a
    // rejected transition (or any server error) silently does nothing.
    type TransitionKey = 'publish' | 'unpublish' | 'reopen' | 'close';
    interface PendingTransition {
        key: TransitionKey;
        status: NeedStatus;
        title: string;
        description: React.ReactNode;
        confirmLabel: string;
        destructive?: boolean;
        failMsg: string;
    }
    const [pending, setPending] = useState<PendingTransition | null>(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [relistOpen, setRelistOpen] = useState(false);

    const confirmTransition = async () => {
        if (!need || !pending) return;
        setActionLoading(pending.key);
        try {
            const result = await updateNeedStatus(need.id, pending.status);
            if (!result) { toast({ title: pending.failMsg, variant: 'destructive' }); return; }
            setPending(null);
            await loadNeed();
        } catch { toast({ title: pending.failMsg, variant: 'destructive' }); }
        finally { setActionLoading(null); }
    };

    const askPublish = () => setPending({
        key: 'publish', status: 'active', confirmLabel: 'Publicera',
        title: 'Publicera annonsen?',
        description: 'Annonsen blir synlig för kandidater och börjar ta emot ansökningar.',
        failMsg: 'Kunde inte publicera annonsen',
    });
    const askReopen = () => setPending({
        key: 'reopen', status: 'active', confirmLabel: 'Återpublicera',
        title: 'Återpublicera annonsen?',
        description: 'Annonsen blir synlig igen och kan ta emot ansökningar.',
        failMsg: 'Kunde inte återöppna annonsen',
    });
    const askUnpublish = () => setPending({
        key: 'unpublish', status: 'paused', confirmLabel: 'Avpublicera',
        title: 'Avpublicera annonsen?',
        description: 'Annonsen döljs för kandidater och slutar ta emot ansökningar. Du kan återpublicera när som helst.',
        failMsg: 'Kunde inte avpublicera annonsen',
    });
    const askClose = () => setPending({
        key: 'close', status: 'closed', confirmLabel: 'Stäng annons', destructive: true,
        title: 'Stäng annonsen?',
        description: 'Annonsen avslutas och tas bort från publika listor. Du kan återpublicera senare om du behöver.',
        failMsg: 'Kunde inte stänga annonsen',
    });

    const handleDeleteNeed = async () => {
        if (!need) return;
        try { setActionLoading('delete'); await deleteNeed(need.id); navigate('/dashboard/needs'); }
        catch { toast({ title: 'Kunde inte radera annonsen', variant: 'destructive' }); setActionLoading(null); }
    };

    if (loading) return <LoadingState message="Laddar annons..." />;
    if (error || !need) return <ErrorState title="Kunde inte ladda annonsen" message={error || 'Annonsen hittades inte'} onRetry={handleBack} />;

    return (
        <div className={NEXILINK_SPACING}>
            <button onClick={handleBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
                <ArrowLeft className="w-4 h-4" /> <span className="text-sm font-medium">Tillbaka till rekrytering</span>
            </button>

            <div className={`${NEXILINK_CARD_WHITE_ELEVATED} p-4 sm:p-6`}>
                <NeedView
                    data={needToView(need, employerDetails?.organisation)}
                    statusBadge={<StatusBadge status={need.status} label={getNeedStatusLabel(need.status)} />}
                    actions={
                        // SAME shared builder as the Rekrytering list ⋮ → the two menus
                        // are guaranteed identical. Chatta med Link lives in the menu.
                        <EntityActionMenu
                            actions={toEntityActions(buildNeedMenuItems(need, {
                                onEdit: () => setIsEditModalOpen(true),
                                onChatWithLink: () => stageLink({
                                    reference: { type: 'need', id: need.id },
                                    label: need.role,
                                    prompt: 'Hjälp mig att jobba vidare med den här annonsen.',
                                }),
                                onPreview: () => navigate(`/dashboard/needs/${need.id}/preview`),
                                onDuplicate: () => setIsDuplicateModalOpen(true),
                                onCopyLink: copyPublicLink,
                                onPublish: askPublish,
                                onUnpublish: askUnpublish,
                                onReopen: askReopen,
                                onRelist: () => setRelistOpen(true),
                                onClose: askClose,
                                onDelete: () => setConfirmDelete(true),
                                onDevExpire: async () => { try { await needApi.devExpireNeed(need.id); await loadNeed(); } catch { /* dev-only */ } },
                            }))}
                        />
                    }
                >
                    {/* Attachments (read-only) */}
                    {(requiredCourses.length > 0 || programName) && (
                        <div className="space-y-3">
                            {requiredCourses.length > 0 && (
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2"><BookOpen className="w-4 h-4 text-[#1A4D4F]" /> Förkunskapsmoduler</h3>
                                    <div className="flex flex-wrap gap-1.5">
                                        {requiredCourses.map((name, i) => (
                                            <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-700">{name}</span>
                                        ))}
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1">Kandidaten måste genomföra dessa innan ansökan.</p>
                                </div>
                            )}
                            {programName && (
                                <div className="p-3 rounded-xl bg-[#A4C3B2]/15 border border-[#A4C3B2]/50">
                                    <p className="text-sm font-medium text-gray-900 flex items-center gap-2"><GraduationCap className="w-4 h-4 text-[#1A4D4F]" /> Onboarding för rollen: {programName}</p>
                                    <p className="text-xs text-gray-500 mt-0.5">Tilldelas automatiskt vid anställning.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Status actions live in the ⋮ menu in the header (not a row of
                        big buttons) — see the EntityActionMenu above. */}

                    {/* Applications — inline (no separate page) */}
                    <div className="pt-2 border-t border-gray-100">
                        <NeedApplicationsSection needId={need.id} />
                    </div>
                </NeedView>
            </div>

            <NeedFormModal mode="edit" open={isEditModalOpen} onOpenChange={setIsEditModalOpen} existingNeed={need} onSuccess={handleEditSuccess} />
            <NeedFormModal mode="duplicate" open={isDuplicateModalOpen} onOpenChange={setIsDuplicateModalOpen} existingNeed={need} onSuccess={handleDuplicateSuccess} />

            {/* #64 — confirm on every status transition. */}
            <ConfirmDialog
                open={pending !== null}
                onOpenChange={(o) => { if (!o) setPending(null); }}
                title={pending?.title ?? ''}
                description={pending?.description}
                confirmLabel={pending?.confirmLabel ?? 'Bekräfta'}
                destructive={pending?.destructive}
                isBusy={actionLoading === pending?.key}
                onConfirm={confirmTransition}
            />

            {/* Radera — destructive, irreversible. */}
            <ConfirmDialog
                open={confirmDelete}
                onOpenChange={setConfirmDelete}
                title="Radera annonsen?"
                description="Annonsen tas bort permanent. Detta går inte att ångra."
                confirmLabel="Radera annons"
                destructive
                isBusy={actionLoading === 'delete'}
                onConfirm={async () => { setConfirmDelete(false); await handleDeleteNeed(); }}
            />

            {/* #281 — relist an expired posting with a new deadline (shared dialog). */}
            <RelistNeedDialog need={need} open={relistOpen} onOpenChange={setRelistOpen} onDone={loadNeed} />
        </div>
    );
};

export default NeedDetail;
